import { RefObject, KeyboardEventHandler, useEffect, useCallback } from 'react';

const getFocusableChildElements = (element: HTMLElement) => {
  const elements = Array.from<HTMLElement>(
    element.querySelectorAll(
      'a[href], button, details, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  );

  return elements.filter((e) => !e.hasAttribute('disabled'));
};

const focusNextElement = (elements: HTMLElement[]) => {
  if (elements.length === 0) {
    return;
  }

  let foundActiveElement = false;

  for (let index = 0; index < elements.length; index++) {
    const element = elements[index];
    if (element === document.activeElement) {
      foundActiveElement = true;
      if (index === elements.length - 1) {
        elements[0].focus();
      } else {
        elements[index + 1].focus();
      }
      break;
    }
  }

  if (!foundActiveElement) {
    elements[0].focus();
  }
};

const focusPreviousElement = (elements: HTMLElement[]) => {
  if (elements.length === 0) {
    return;
  }

  let foundActiveElement = false;

  for (let index = elements.length - 1; index >= 0; index--) {
    const element = elements[index];
    if (element === document.activeElement) {
      foundActiveElement = true;
      if (index === 0) {
        elements[elements.length - 1].focus();
      } else {
        elements[index - 1].focus();
      }
      break;
    }
  }

  if (!foundActiveElement) {
    elements[elements.length - 1].focus();
  }
};

/**
 * Returns a function that restricts tab focus to child elements of the ref object
 */
const useFocusTrap = (
  ref: RefObject<HTMLElement>,
  disabled = false
): KeyboardEventHandler<HTMLElement> => {
  const handleTab = useCallback<KeyboardEventHandler<HTMLElement>>(
    (event) => {
      if (!disabled && event.key === 'Tab' && ref.current) {
        event.preventDefault();
        const elements = getFocusableChildElements(ref.current);
        if (event.shiftKey) {
          focusPreviousElement(elements);
        } else {
          focusNextElement(elements);
        }
      }
    },
    [disabled, ref]
  );

  useEffect(() => {
    if (!disabled && ref.current) {
      const elements = getFocusableChildElements(ref.current);
      if (elements.length > 0) {
        elements[0].focus();
      }
    }
  }, [disabled, ref]);

  return handleTab;
};

export default useFocusTrap;
